import { ArrowDownIcon, ArrowUpIcon, PencilSquareIcon, TrashIcon } from '@heroicons/react/24/outline'
import cloneDeep from 'clone-deep'
import type { InputType } from 'config'
import type { ReactNode } from 'react'
import { PlainTable } from 'stories/components'
import { confirm } from 'utils/modals/confirm'
import { openInputsFormDlg } from 'utils/modals/inputs-form'
import { RenderInput } from 'utils/RenderInput'

function formatTableCell(value: any, input: InputType): string | number {
  if (value == null) return ''
  if (typeof value === 'boolean') return value ? 'Yes' : 'No'
  if (typeof value === 'object') return JSON.stringify(value)
  if (input.options && !Array.isArray(input.options)) return input.options[value] ?? value
  return value as string | number
}

export interface InputsTableProps {
  title?: string
  /** Renders in the section header row (e.g. “Does not apply” checkbox). */
  additionalElements?: {
    title?: ReactNode
  }
  inputs: Record<string, InputType>
  rows?: Record<string, any>[]
  onUpdate: (rows: Record<string, any>[]) => void
  columnKeys: string[]
  columnHeaders?: Record<string, string>
  addRowTitle?: string
  editRowTitle?: string
  confirmRemoveMessage?: string
  formChildLevel?: number
  /** Passed to add/edit form dialog grid wrapper (see `openInputsFormDlg` `formClassName`). */
  formClassName?: string
  /** When true, row actions and Add are disabled (view-only). */
  readOnly?: boolean
  /** Pre-filled values for the add-row dialog (e.g. readonly borrower, employer). */
  defaultAddValues?: Record<string, any>
  /** Map stored row to form values before edit (e.g. refresh readonly labels from current loan state). */
  getEditFormValues?: (row: Record<string, any>, index: number) => Record<string, any>
  /**
   * When set, Add opens this instead of the default form dialog (e.g. custom modal for complex rows).
   * Use with onCustomRowEdit; reorder/remove use onUpdate as usual.
   */
  onCustomAdd?: () => void | Promise<void>
  onCustomRowEdit?: (rowIndex: number) => void | Promise<void>
  /**
   * Override what is shown in a read-only table cell (e.g. computed or formatted columns).
   */
  getCellValue?: (columnKey: string, row: Record<string, any>, rowIndex: number) => unknown
  historyFieldKeys?: string[]
  onShowRowFieldHistory?: (rowIndex: number, fieldKey: string, childLevel: number) => void
}

export const InputsTable = ({
  title,
  additionalElements = {
    title: null,
  },
  inputs,
  rows = [],
  onUpdate,
  columnKeys,
  columnHeaders = {},
  addRowTitle = 'Add',
  editRowTitle = 'Edit',
  confirmRemoveMessage = 'Are you sure you want to remove this row?',
  formChildLevel = 2,
  formClassName,
  readOnly = false,
  defaultAddValues = undefined,
  getEditFormValues,
  onCustomAdd,
  onCustomRowEdit,
  getCellValue,
  historyFieldKeys,
  onShowRowFieldHistory,
}: InputsTableProps) => {
  const onAdd = async () => {
    if (readOnly) return
    if (onCustomAdd) {
      await onCustomAdd()
      return
    }
    const result: unknown = await openInputsFormDlg({
      title: addRowTitle,
      inputs,
      values: { ...(defaultAddValues || {}) },
      titleOkay: 'Save',
      childLevel: formChildLevel,
      formClassName,
    })
    if (result === false || typeof result !== 'object' || result === null) return
    onUpdate([
      ...rows,
      {
        id: Date.now(),
        ...(result as Record<string, any>),
      },
    ])
  }

  const onEdit = async (index: number) => {
    if (readOnly) return
    if (onCustomRowEdit) {
      await onCustomRowEdit(index)
      return
    }
    const base = cloneDeep(rows[index])
    const formValues = getEditFormValues ? getEditFormValues(base, index) : base

    let editInputs = inputs
    let showHistory: ((key: string) => void) | undefined
    if (onShowRowFieldHistory && historyFieldKeys && historyFieldKeys.length) {
      editInputs = { ...inputs }
      for (const key of historyFieldKeys) {
        if (editInputs[key]) editInputs[key] = { ...editInputs[key], history: true }
      }
      showHistory = (key: string) => onShowRowFieldHistory(index, key, formChildLevel + 1)
    }

    const result: unknown = await openInputsFormDlg({
      title: editRowTitle,
      inputs: editInputs,
      values: formValues,
      titleOkay: 'Save',
      childLevel: formChildLevel,
      formClassName,
      showHistory,
    })
    if (result === false || typeof result !== 'object' || result === null) return
    const nextValues = result as Record<string, any>
    const nextRows = [...rows]
    nextRows[index] = { ...nextRows[index], ...nextValues }
    onUpdate(nextRows)
  }

  const onRemove = async (index: number) => {
    if (readOnly) return
    const ok = await confirm(confirmRemoveMessage, {}, formChildLevel)
    if (!ok) return
    onUpdate(rows.filter((_, j) => j !== index))
  }

  const onMove = (index: number, direction: 'up' | 'down') => {
    if (readOnly) return
    if (rows.length < 2) return
    if (direction === 'up' && index <= 0) return
    if (direction === 'down' && index >= rows.length - 1) return
    const nextRows = [...rows]
    const swapWith = direction === 'up' ? index - 1 : index + 1
    ;[nextRows[index], nextRows[swapWith]] = [nextRows[swapWith], nextRows[index]]
    onUpdate(nextRows)
  }

  const header: (string | JSX.Element)[] = [
    'No',
    ...columnKeys.map((key) => columnHeaders[key] || (inputs[key] as any)?.title || key),
    <div className="flex justify-end">
      {!readOnly && (
        <button type="button" className="btn-link" onClick={() => void onAdd()}>
          + Add
        </button>
      )}
    </div>,
  ]

  const tableData: Array<Array<string | number | JSX.Element | undefined> | null> = rows.map((row, rowIndex) => {
    const atFirst = rowIndex === 0
    const atLast = rowIndex === rows.length - 1
    return [
      rowIndex + 1,
      ...columnKeys.map((k) => {
        const o = (row as any)[k]
        if (!getCellValue) return formatTableCell(o, inputs[k])
        const v = getCellValue(k, row, rowIndex)
        const raw = v !== undefined ? v : o
        return formatTableCell(raw, inputs[k])
      }),
      <div className="flex items-center justify-end gap-0.5">
        <button
          type="button"
          className="p-1 rounded hover:bg-gray-200 text-gray-700 disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:bg-transparent"
          title="Move up"
          disabled={readOnly || atFirst}
          onClick={() => onMove(rowIndex, 'up')}
          aria-label="Move row up"
        >
          <ArrowUpIcon className="w-4 h-4" />
        </button>
        <button
          type="button"
          className="p-1 rounded hover:bg-gray-200 text-gray-700 disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:bg-transparent"
          title="Move down"
          disabled={readOnly || atLast}
          onClick={() => onMove(rowIndex, 'down')}
          aria-label="Move row down"
        >
          <ArrowDownIcon className="w-4 h-4" />
        </button>
        <button
          type="button"
          className="p-1 rounded hover:bg-gray-200 text-sky-600 disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:bg-transparent"
          title="Edit"
          disabled={readOnly}
          onClick={() => void onEdit(rowIndex)}
          aria-label="Edit row"
        >
          <PencilSquareIcon className="w-4 h-4" />
        </button>
        <button
          type="button"
          className="p-1 rounded hover:bg-gray-200 text-red-600 disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:bg-transparent"
          title="Remove"
          disabled={readOnly}
          onClick={() => void onRemove(rowIndex)}
          aria-label="Remove row"
        >
          <TrashIcon className="w-4 h-4" />
        </button>
      </div>,
    ]
  })

  return (
    <>
      {title && (
        <div className="mb-2">
          <RenderInput
            input={{ inputType: 'section', title, additionalElements: additionalElements.title } as InputType}
            onChange={() => {}}
            Key="title"
          />
        </div>
      )}
      <PlainTable header={header} data={tableData} isLastColumnStatic thClass="text-xs" />
    </>
  )
}

export * from './InputsTableWithNoApply'
